import { apiService } from './api';

export type OrganizationType = 'INDIVIDUAL_LANDLORD' | 'PROPERTY_MANAGEMENT_FIRM';
export type OrganizationStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
export type OrgMemberRole = 'OWNER' | 'ADMIN' | 'AGENT' | 'CARETAKER' | 'MEMBER';
export type PropertyOwnerType = 'PERSON' | 'COMPANY';

export interface OrganizationProfile {
  id: string;
  name: string;
  slug: string;
  type: OrganizationType;
  status: OrganizationStatus;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  primaryCity?: string | null;
  onboardingCompletedAt?: string | null;
  ownerUserId?: string | null;
  members?: Array<{
    id: string;
    userId: string;
    role: OrgMemberRole;
    status: string;
  }>;
}

export interface OrganizationMembership {
  organizationId: string;
  role: OrgMemberRole;
  status: string;
}

export interface OrganizationResponse {
  organization: OrganizationProfile;
  membership: OrganizationMembership;
}

export interface PropertyOwner {
  id: string;
  organizationId: string;
  type: PropertyOwnerType;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count?: { properties: number; payHeroChannels: number };
}

export const organizationService = {
  async getMine(): Promise<OrganizationResponse> {
    const res = await apiService.get<OrganizationResponse>('/organization');
    return res.data;
  },

  async updateProfile(patch: {
    name?: string;
    type?: OrganizationType;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    primaryCity?: string | null;
  }): Promise<OrganizationProfile> {
    const res = await apiService.patch<{ organization: OrganizationProfile }>('/organization', patch);
    return res.data.organization;
  },

  async completeOnboarding(): Promise<OrganizationProfile> {
    const res = await apiService.post<{ organization: OrganizationProfile }>(
      '/organization/complete-onboarding'
    );
    return res.data.organization;
  },

  async listPropertyOwners(): Promise<PropertyOwner[]> {
    const res = await apiService.get<{ propertyOwners: PropertyOwner[] }>(
      '/organization/property-owners'
    );
    return res.data.propertyOwners || [];
  },

  async createPropertyOwner(input: {
    name: string;
    type?: PropertyOwnerType;
    email?: string | null;
    phone?: string | null;
    notes?: string | null;
  }): Promise<PropertyOwner> {
    const res = await apiService.post<{ propertyOwner: PropertyOwner }>(
      '/organization/property-owners',
      input
    );
    return res.data.propertyOwner;
  },

  async updatePropertyOwner(
    ownerId: string,
    input: {
      name?: string;
      type?: PropertyOwnerType;
      email?: string | null;
      phone?: string | null;
      notes?: string | null;
      isDefault?: boolean;
    }
  ): Promise<PropertyOwner> {
    const res = await apiService.patch<{ propertyOwner: PropertyOwner }>(
      `/organization/property-owners/${ownerId}`,
      input
    );
    return res.data.propertyOwner;
  },

  async deletePropertyOwner(ownerId: string): Promise<{
    success: boolean;
    deletedId: string;
    reassignedToId: string;
    propertiesMoved: number;
    channelsMoved: number;
  }> {
    const res = await apiService.delete<{
      success: boolean;
      deletedId: string;
      reassignedToId: string;
      propertiesMoved: number;
      channelsMoved: number;
    }>(`/organization/property-owners/${ownerId}`);
    return res.data;
  },
};
